import { formatTimestamp } from './date.helper';
import { RUN_STATUS } from '../types/global.enum';

export interface SupportRunOverviewContext {
  projectId: number;
  projectName?: string;
  testCaseId?: number | string;
  testCaseName?: string;
  runId: number | string;
  status?: string;
  channel?: string;
  startTime?: string | number | Date | null;
  endTime?: string | number | Date | null;
  voiceMos?: number | string | null;
  browserType?: string | null;
  callerId?: string | null;
  scheduleName?: string | null;
  segments?: Array<any> | null;
  resultUrl?: string;
}

export interface SupportRunOverviewPayload {
  summary: string;
  description: string;
}

const stepSuccessStatus =
  typeof RUN_STATUS.SUCCESS === 'string' ? RUN_STATUS.SUCCESS.toUpperCase() : undefined;

const STEP_SUCCESS_STATUSES = new Set<string>(
  ['PASS', 'SUCCESS', stepSuccessStatus].filter(Boolean) as string[],
);

const STATUS_SUMMARY_MAP: Record<string, string> = {
  FAILED: 'failed',
  FAIL: 'failed',
  PASS: 'passed',
  SUCCESS: 'passed',
  TIMEOUT_STOPPED: 'timed out',
  FORCE_STOPPED: 'was force stopped',
  RUNNING: 'is running',
};

export const buildSupportRunOverview = (
  context: SupportRunOverviewContext,
): SupportRunOverviewPayload => {
  const {
    projectId,
    projectName,
    testCaseId,
    testCaseName,
    runId,
    status,
    channel,
    startTime,
    endTime,
    voiceMos,
    browserType,
    callerId,
    scheduleName,
    segments,
    resultUrl,
  } = context;

  const resolvedProjectName = projectName || 'N/A';
  const resolvedTestCaseName = testCaseName || 'Test case';
  const statusUpper = typeof status === 'string' ? status.toUpperCase() : '';
  const descriptor = STATUS_SUMMARY_MAP[statusUpper] || (statusUpper ? statusUpper.toLowerCase() : 'completed');
  const summaryStatusSuffix = STATUS_SUMMARY_MAP[statusUpper] ? '' : statusUpper ? ` (${statusUpper})` : '';
  const summary = `Test case "${resolvedTestCaseName}" ${descriptor}${summaryStatusSuffix}`;

  const overviewLines: string[] = ['*Run overview*'];
  overviewLines.push(`- Project: ${resolvedProjectName} (ID: ${projectId})`);

  if (testCaseId !== undefined && testCaseId !== null && `${testCaseId}`.trim() !== '') {
    overviewLines.push(`- Test case ID: ${testCaseId}`);
  }

  overviewLines.push(`- Result ID: ${runId}`);
  overviewLines.push(`- Status: ${statusUpper || 'UNKNOWN'}`);

  if (channel) {
    overviewLines.push(`- Channel: ${channel}`);
  }

  if (startTime) {
    overviewLines.push(`- Started: ${formatTimestamp(startTime)}`);
  }

  if (endTime) {
    overviewLines.push(`- Completed: ${formatTimestamp(endTime)}`);
  }

  if (startTime && endTime) {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    const durationSeconds = (endDate.getTime() - startDate.getTime()) / 1000;
    if (Number.isFinite(durationSeconds) && durationSeconds >= 0) {
      overviewLines.push(`- Duration: ${durationSeconds.toFixed(2)}s`);
    }
  }

  if (voiceMos !== undefined && voiceMos !== null && `${voiceMos}`.trim() !== '') {
    overviewLines.push(`- MOS: ${voiceMos}`);
  }

  if (browserType) {
    overviewLines.push(`- Browser: ${browserType}`);
  }

  if (callerId) {
    overviewLines.push(`- Caller ID: ${callerId}`);
  }

  if (scheduleName) {
    overviewLines.push(`- Schedule: ${scheduleName}`);
  }

  if (resultUrl) {
    overviewLines.push(`- Result URL: ${resultUrl}`);
  }

  let totalSteps = 0;
  let failedSteps = 0;
  const failureHighlights: string[] = [];

  if (Array.isArray(segments)) {
    segments.forEach((segment, segmentIndex) => {
      const segmentName = segment?.name || `Segment ${segmentIndex + 1}`;
      const steps = Array.isArray(segment?.steps) ? segment.steps : [];

      steps.forEach((step: any) => {
        totalSteps += 1;
        const stepStatus = step?.testResult?.status;
        const stepStatusUpper = typeof stepStatus === 'string' ? stepStatus.toUpperCase() : '';
        const isSuccess = STEP_SUCCESS_STATUSES.has(stepStatusUpper as any);

        if (stepStatusUpper && !isSuccess) {
          failedSteps += 1;
          const stepLabel =
            step?.stepSeq !== undefined && step?.stepSeq !== null
              ? `Step ${step.stepSeq}`
              : step?.testStepId
              ? `Step ${step.testStepId}`
              : step?.stepType || 'Step';

          const messageParts = [
            step?.testResult?.message,
            step?.testResult?.messageToPlay,
            step?.testResult?.voice?.utterance,
            step?.remark,
          ].filter(Boolean);
          const messageText = messageParts.join(' | ');
          failureHighlights.push(
            `- ${segmentName} › ${stepLabel}: ${stepStatusUpper}${messageText ? ` — ${messageText}` : ''}`,
          );
        }
      });
    });
  }

  if (totalSteps > 0) {
    overviewLines.push(`- Steps failed: ${failedSteps}/${totalSteps}`);
  }

  const descriptionLines = [...overviewLines];

  if (failureHighlights.length > 0) {
    descriptionLines.push('');
    descriptionLines.push('*Key failure details*');
    failureHighlights.slice(0, 5).forEach((item) => descriptionLines.push(item));
    if (failureHighlights.length > 5) {
      descriptionLines.push(`- …and ${failureHighlights.length - 5} more.`);
    }
  } else if (totalSteps > 0) {
    descriptionLines.push('');
    descriptionLines.push('*All executed steps completed without reported failures.*');
  }

  return {
    summary,
    description: descriptionLines.join('\n'),
  };
};
